/**
 * EndpointPicker — searchable, paginated endpoint selector backed by
 * the ABAC-scoped endpoint metadata API. Ported from origin/dashboard for the feedback
 * form's "affected endpoint" field; restyled to origin's CSS-var palette
 * and rectangular controls. Renders the dropdown in a portal so it
 * escapes the form's overflow clipping.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { listEndpoints } from "../lib/reportClient.js";
import { endpointIdentityBadge } from "../lib/endpointDedupe.js";
import { NOTE_CLASS } from "../lib/ui.js";
import { isSurfaceDenied, useMyPermissions } from "../lib/permissions.js";

const PAGE_SIZE = 50;
const DEBOUNCE_MS = 300;

function endpointIdOf(endpoint) {
    return endpoint?.endpoint_id || endpoint?.endpointId || endpoint?.id || "";
}

function endpointBaseLabel(endpoint) {
    const hostname =
        endpoint?.hostname ||
        endpoint?.name ||
        endpoint?.metadata?.hostInfo?.hostname ||
        endpoint?.metadata?.host_info?.hostname ||
        "";
    const serial = endpoint?.serial || endpoint?.serial_number || endpoint?.serialNumber || "";
    return [hostname || "Unnamed endpoint", serial].filter(Boolean).join(" · ");
}

function endpointLabel(endpoint, disambiguate = false) {
    const base = endpointBaseLabel(endpoint);
    const badge = endpointIdentityBadge(endpoint);
    const id = endpointIdOf(endpoint);
    return [
        base,
        badge?.label,
        disambiguate && id ? String(id).slice(0, 8) : "",
    ].filter(Boolean).join(" · ");
}

function useDebouncedValue(value, ms) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const handle = setTimeout(() => setDebounced(value), ms);
        return () => clearTimeout(handle);
    }, [value, ms]);
    return debounced;
}

function errorMessage(error) {
    if (!error) return "";
    if (error.details && typeof error.details === "string") return error.details;
    if (error.message) return error.message;
    return "Request failed";
}

const INPUT_CLASS_BASE =
    "min-h-[30px] w-full rounded-[2px] border bg-transparent px-[9px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]";

function inputClasses(error) {
    return `${INPUT_CLASS_BASE} ${error ? "border-red-400/60" : "border-[color:var(--line)] focus:border-[color:var(--line-strong)]"}`;
}

export default function EndpointPicker({ value, onChange, error }) {
    const caps = useMyPermissions();
    const denied = isSurfaceDenied(caps, "ListEndpoints");
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const [results, setResults] = useState([]);
    const [nextPage, setNextPage] = useState(0);
    const [total, setTotal] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [loadError, setLoadError] = useState("");
    const [highlightIndex, setHighlightIndex] = useState(-1);
    const [selectedEndpoint, setSelectedEndpoint] = useState(null);
    const [panelRect, setPanelRect] = useState(null);

    const inputRef = useRef(null);
    const panelRef = useRef(null);
    const listRef = useRef(null);
    const sentinelRef = useRef(null);
    const highlightedRowRef = useRef(null);
    const sequenceIdRef = useRef(0);
    const pendingQueryRef = useRef("");

    const debouncedQuery = useDebouncedValue(query, DEBOUNCE_MS);
    const visibleResults = results;
    const visibleLabelCounts = useMemo(() => {
        const counts = new Map();
        for (const endpoint of visibleResults) {
            const label = endpointBaseLabel(endpoint);
            counts.set(label, (counts.get(label) ?? 0) + 1);
        }
        return counts;
    }, [visibleResults]);

    useEffect(() => {
        if (!value) {
            setSelectedEndpoint(null);
            return;
        }
        setSelectedEndpoint((current) =>
            current && endpointIdOf(current) === value ? current : null,
        );
    }, [value]);

    const updatePanelRect = useCallback(() => {
        if (!inputRef.current) return;
        const rect = inputRef.current.getBoundingClientRect();
        setPanelRect({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }, []);

    useEffect(() => {
        if (!open) {
            setPanelRect(null);
            return undefined;
        }
        updatePanelRect();
        const handle = () => updatePanelRect();
        window.addEventListener("scroll", handle, true);
        window.addEventListener("resize", handle);
        return () => {
            window.removeEventListener("scroll", handle, true);
            window.removeEventListener("resize", handle);
        };
    }, [open, updatePanelRect]);

    useEffect(() => {
        if (!open) return undefined;
        const handle = (event) => {
            if (inputRef.current?.contains(event.target)) return;
            if (panelRef.current?.contains(event.target)) return;
            setOpen(false);
        };
        document.addEventListener("mousedown", handle);
        return () => document.removeEventListener("mousedown", handle);
    }, [open]);

    const resetAndFetch = useCallback(async (queryForReset) => {
        const seq = ++sequenceIdRef.current;
        pendingQueryRef.current = queryForReset;
        setResults([]);
        setNextPage(0);
        setTotal(null);
        setLoadError("");
        setHighlightIndex(-1);
        setLoading(true);
        setLoadingMore(false);
        try {
            const response = await listEndpoints({ page: 0, pageSize: PAGE_SIZE, hostname: queryForReset || undefined });
            if (sequenceIdRef.current !== seq) return;
            const items = Array.isArray(response?.endpoints) ? response.endpoints : [];
            const filtered = items.filter((endpoint) => endpointIdOf(endpoint));
            const respTotal = Number(response?.total);
            setResults(filtered);
            setTotal(Number.isFinite(respTotal) ? respTotal : filtered.length);
            setNextPage(1);
        } catch (err) {
            if (sequenceIdRef.current !== seq) return;
            setLoadError(errorMessage(err) || "Failed to load endpoints");
        } finally {
            if (sequenceIdRef.current === seq) setLoading(false);
        }
    }, []);

    const loadMore = useCallback(async () => {
        if (loading || loadingMore || loadError) return;
        if (total != null && results.length >= total) return;
        const seq = sequenceIdRef.current;
        const queryForMore = pendingQueryRef.current;
        const pageToLoad = nextPage;
        setLoadingMore(true);
        try {
            const response = await listEndpoints({ page: pageToLoad, pageSize: PAGE_SIZE, hostname: queryForMore || undefined });
            if (sequenceIdRef.current !== seq) return;
            const items = Array.isArray(response?.endpoints) ? response.endpoints : [];
            const filtered = items.filter((endpoint) => endpointIdOf(endpoint));
            const respTotal = Number(response?.total);
            setResults((current) => [...current, ...filtered]);
            if (Number.isFinite(respTotal)) setTotal(respTotal);
            setNextPage(pageToLoad + 1);
        } catch (err) {
            if (sequenceIdRef.current !== seq) return;
            setLoadError(errorMessage(err) || "Failed to load more endpoints");
        } finally {
            if (sequenceIdRef.current === seq) setLoadingMore(false);
        }
    }, [loading, loadingMore, loadError, total, results.length, nextPage]);

    useEffect(() => {
        if (!open || denied) return;
        resetAndFetch(debouncedQuery);
    }, [open, denied, debouncedQuery, resetAndFetch]);

    useEffect(() => {
        if (!open) return undefined;
        const list = listRef.current;
        const sentinel = sentinelRef.current;
        if (!list || !sentinel) return undefined;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) loadMore();
            },
            { root: list, threshold: 0.1 },
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [open, loadMore, results.length]);

    useEffect(() => {
        if (highlightIndex < 0) return;
        highlightedRowRef.current?.scrollIntoView({ block: "nearest" });
    }, [highlightIndex]);

    const handleSelect = useCallback((endpoint) => {
        const id = endpointIdOf(endpoint);
        if (!id) return;
        setSelectedEndpoint(endpoint);
        setQuery("");
        setOpen(false);
        onChange(id);
    }, [onChange]);

    const handleClear = useCallback(() => {
        setSelectedEndpoint(null);
        setQuery("");
        onChange("");
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 0);
    }, [onChange]);

    const onInputKeyDown = (event) => {
        if (event.key === "ArrowDown") {
            event.preventDefault();
            if (!open) { setOpen(true); return; }
            setHighlightIndex((i) => Math.min(i + 1, visibleResults.length - 1));
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setHighlightIndex((i) => Math.max(i - 1, -1));
        } else if (event.key === "Enter") {
            if (open && highlightIndex >= 0 && visibleResults[highlightIndex]) {
                event.preventDefault();
                handleSelect(visibleResults[highlightIndex]);
            }
        } else if (event.key === "Escape") {
            if (open) {
                event.preventDefault();
                setOpen(false);
                inputRef.current?.blur();
            }
        }
    };

    const chipLabel = useMemo(() => {
        if (selectedEndpoint) return endpointLabel(selectedEndpoint);
        if (value) return `Endpoint ${String(value).slice(0, 8)}…`;
        return "";
    }, [selectedEndpoint, value]);

    const remaining = total != null ? Math.max(total - results.length, 0) : null;

    if (denied) {
        return (
            <div className="grid grid-cols-1 gap-[4px]">
                <span className={NOTE_CLASS}>Affected endpoint</span>
                <span className={`${NOTE_CLASS} text-[11px]`}>Endpoint selection is unavailable for a subject-scoped account.</span>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 gap-[4px]">
            <span className={NOTE_CLASS}>Affected endpoint</span>
            {value ? (
                <div
                    className={`flex min-h-[30px] w-full items-center justify-between gap-[8px] rounded-[2px] border px-[9px] text-[12px] leading-[1.18] text-[color:var(--ink)] ${
                        error ? "border-red-400/60" : "border-[color:var(--line)]"
                    }`}
                >
                    <div className="min-w-0 flex-1 truncate">{chipLabel}</div>
                    <button
                        type="button"
                        onClick={handleClear}
                        aria-label="Clear selected endpoint"
                        className="shrink-0 text-[15px] leading-none text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer"
                    >
                        ×
                    </button>
                </div>
            ) : (
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(event) => {
                        setQuery(event.target.value);
                        if (!open) setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={onInputKeyDown}
                    placeholder="Search endpoints by hostname"
                    autoComplete="off"
                    className={inputClasses(error)}
                />
            )}
            {error ? <div className="text-[11px] leading-[1.18] text-red-400">{error}</div> : null}
            {open && !value && panelRect && typeof document !== "undefined"
                ? createPortal(
                    <div
                        ref={panelRef}
                        style={{ position: "fixed", top: panelRect.top, left: panelRect.left, width: panelRect.width, zIndex: 50 }}
                        className="overflow-hidden rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--bg)] shadow-lg"
                    >
                        <div
                            ref={listRef}
                            className="max-h-[320px] overflow-y-auto"
                            role="listbox"
                            aria-busy={loading || loadingMore ? "true" : undefined}
                        >
                            {loading && visibleResults.length === 0 ? (
                                <div className={`px-[10px] py-[10px] text-[12px] ${NOTE_CLASS}`}>Loading endpoints…</div>
                            ) : loadError && visibleResults.length === 0 ? (
                                <button
                                    type="button"
                                    onClick={() => resetAndFetch(pendingQueryRef.current)}
                                    className="block w-full px-[10px] py-[10px] text-left text-[12px] text-red-400 hover:text-red-300 cursor-pointer"
                                >
                                    {loadError}. Click to retry.
                                </button>
                            ) : visibleResults.length === 0 ? (
                                <div className={`px-[10px] py-[10px] text-[12px] ${NOTE_CLASS}`}>
                                    {pendingQueryRef.current
                                        ? `No endpoints match "${pendingQueryRef.current}".`
                                        : "No endpoints found."}
                                </div>
                            ) : (
                                <>
                                    {visibleResults.map((endpoint, index) => {
                                        const id = endpointIdOf(endpoint);
                                        const highlighted = index === highlightIndex;
                                        return (
                                            <button
                                                key={`${id}-${index}`}
                                                ref={highlighted ? highlightedRowRef : null}
                                                type="button"
                                                role="option"
                                                aria-selected={highlighted}
                                                onMouseEnter={() => setHighlightIndex(index)}
                                                onClick={() => handleSelect(endpoint)}
                                                className={`block w-full truncate px-[10px] py-[6px] text-left text-[12px] cursor-pointer ${
                                                    highlighted
                                                        ? "bg-[color:var(--control-bg)] text-[color:var(--ink)]"
                                                        : "text-[color:var(--ink)] hover:bg-[color:var(--control-bg)]"
                                                }`}
                                            >
                                                {endpointLabel(
                                                    endpoint,
                                                    (visibleLabelCounts.get(endpointBaseLabel(endpoint)) ?? 0) > 1,
                                                )}
                                            </button>
                                        );
                                    })}
                                    <div ref={sentinelRef} aria-hidden="true" style={{ height: 1 }} />
                                    {loadingMore ? (
                                        <div className={`px-[10px] py-[6px] text-[11px] ${NOTE_CLASS}`}>Loading more…</div>
                                    ) : null}
                                    {loadError && !loadingMore ? (
                                        <button
                                            type="button"
                                            onClick={() => { setLoadError(""); loadMore(); }}
                                            className="block w-full px-[10px] py-[6px] text-left text-[11px] text-red-400 hover:text-red-300 cursor-pointer"
                                        >
                                            {loadError}. Click to retry.
                                        </button>
                                    ) : null}
                                    {!loadingMore && !loadError && total != null && results.length >= total ? (
                                        <div className={`px-[10px] py-[6px] text-[11px] ${NOTE_CLASS}`}>
                                            {visibleResults.length === 0
                                                ? "No endpoints."
                                                : `End of list (${visibleResults.length} ${visibleResults.length === 1 ? "endpoint" : "endpoints"})`}
                                        </div>
                                    ) : null}
                                    {!loadingMore && !loadError && remaining != null && remaining > 0 ? (
                                        <div className={`px-[10px] py-[6px] text-[11px] ${NOTE_CLASS}`}>
                                            {`${results.length} of ${total}. Scroll to load more.`}
                                        </div>
                                    ) : null}
                                </>
                            )}
                        </div>
                    </div>,
                    document.body,
                )
                : null}
        </div>
    );
}
